import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useLocation, useNavigate } from 'react-router-dom';

import { queryApi } from '@/lib/api';
import type { QueryRequest, QueryResponse } from '@/types/api';
import { useChatStore } from './store/chatStore';

const THREAD_KEY = ['threads'] as const;
const TURNS_KEY = (threadId: string) => ['threads', threadId, 'turns'] as const;

export function useThreads() {
  return useQuery({
    queryKey: THREAD_KEY,
    queryFn: () => queryApi.listThreads(),
    staleTime: 5 * 60_000,
  });
}

export function useThreadTurns(threadId: string | null) {
  return useQuery({
    queryKey: threadId ? TURNS_KEY(threadId) : ['threads', 'none'],
    queryFn: () => queryApi.getThread(threadId as string),
    enabled: Boolean(threadId),
    staleTime: 60_000,
  });
}

export function useSubmitQuery() {
  const startSubmission = useChatStore((s) => s.startSubmission);
  const finaliseSubmission = useChatStore((s) => s.finaliseSubmission);
  const failSubmission = useChatStore((s) => s.failSubmission);
  const responseFormat = useChatStore((s) => s.responseFormat);
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const location = useLocation();

  return useMutation({
    mutationFn: async (req: QueryRequest): Promise<{
      pendingTurnId: string;
      response: QueryResponse;
    }> => {
      const pendingTurnId = startSubmission(req.question);
      try {
        // Fall back to the store's activeThreadId when the caller didn't
        // pass a thread_id explicitly. The URL only catches up after the
        // first successful response — without this, a second message
        // submitted before the URL has been navigated would open a new
        // thread on the backend.
        const storeThreadId = useChatStore.getState().activeThreadId;
        const response = await queryApi.submit({
          ...req,
          thread_id: req.thread_id ?? storeThreadId ?? undefined,
          response_format: req.response_format ?? responseFormat,
        });
        return { pendingTurnId, response };
      } catch (e) {
        failSubmission(pendingTurnId, (e as Error).message);
        throw e;
      }
    },
    onSuccess: ({ pendingTurnId, response }) => {
      finaliseSubmission(pendingTurnId, response);
      void queryClient.invalidateQueries({ queryKey: THREAD_KEY });
      if (response.thread_id) {
        void queryClient.invalidateQueries({ queryKey: TURNS_KEY(response.thread_id) });
        // Sync the URL to the freshly-created thread so subsequent
        // refreshes / shares hydrate the same conversation and the
        // sidebar highlights it. `replace` keeps the back button on
        // the prior page rather than logging /app/chat in history.
        const expected = `/app/chat/${response.thread_id}`;
        if (location.pathname !== expected) {
          navigate(expected, { replace: true });
        }
      }
    },
  });
}

export function useFlagTurn() {
  return useMutation({
    mutationFn: ({ turnId, reason }: { turnId: string; reason: string }) =>
      queryApi.flag(turnId, { reason }),
  });
}
